import React, { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { color, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { FaRegCopy, FaWallet } from "react-icons/fa";
import { formatUnits, getAddress, isAddress, ZeroAddress } from "ethers";
import { useActiveAccount, useConnectModal } from "thirdweb/react";

import Smcard from "./smcard";
import TransactionSucessModal from "./Modals/TransactionSucess";
import { Store } from "../context/StoreContext";
import { NotificationContext } from "../context/NotificationContext";
import { useTheme } from "../context/ThemeContext";
import { BRAND } from "../config/brand";
import {
  thirdwebAppMetadata,
  thirdwebClient,
  thirdwebWalletConnectOptions,
  thirdwebWallets,
  bscChain,
} from "../config/thirdweb";
import {
  getPresaleTimeLeft,
  hasPresaleCountdownEnded,
} from "../utils/presaleCountdown";

import icon1 from "../assets/images/icons/sm-1.svg";
import icon2 from "../assets/images/icons/sm-2.svg";
import icon3 from "../assets/images/icons/sm-3.svg";
import icon4 from "../assets/images/icons/sm-4.svg";
import icon5 from "../assets/images/icons/Koin.webp";
import icon6 from "../assets/images/icons/Novadex.jpeg";
import icon7 from "../assets/images/icons/foxbit.svg";
import icon8 from "../assets/images/icons/biswap.svg";
import icon9 from "../assets/images/icons/thena.png";
import icon10 from "../assets/images/icons/gateio.svg";
import icon11 from "../assets/images/icons/babyswap.svg";
import icon12 from "../assets/images/icons/apeswap.svg";
import icon13 from "../assets/images/icons/bitso.svg";
import icon14 from "../assets/images/icons/DODO.webp";
import icon15 from "../assets/images/icons/sushiswap.png";
import { GiTwoCoins, GiWarlockEye, GiWorriedEyes } from "react-icons/gi";

const TOKEN_DECIMALS = 18;

const toDisplayNumber = (rawValue, decimals = TOKEN_DECIMALS) => {
  try {
    return Number(formatUnits(rawValue || "0", decimals));
  } catch {
    return 0;
  }
};

const formatCompact = (value) =>
  Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const formatTokenPrice = (price) => {
  if (!Number.isFinite(price) || price <= 0) return "$0.0000";
  return `$${price.toFixed(price >= 1 ? 3 : 4)}`;
};

const CosmicEyeLoader = ({ label }) => (
  <span className="cosmic-eye-loader" role="status" aria-live="polite">
    <span className="cosmic-eye-loader__mark" aria-hidden="true">
      <span className="cosmic-eye-loader__orbit" />
      <span className="cosmic-eye-loader__core">1</span>
    </span>
    <span className="cosmic-eye-loader__copy">
      <strong>1EYE</strong>
      <small>{label}</small>
    </span>
  </span>
);

const CosmicLoadingOverlay = ({ label }) =>
  createPortal(
    <div
      className="cosmic-loading-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className="cosmic-loading-overlay__content">
        <CosmicEyeLoader label={label} />
        <span className="cosmic-loading-overlay__signal" aria-hidden="true" />
      </div>
    </div>,
    document.body,
  );

function SectionThree({ cardOnly = false }) {
  const { notify } = useContext(NotificationContext);
  const {
    userDatabaseData,
    BuyWithUSDTandUSDC,
    quoteStablePurchase,
    refreshPresaleData,
    addTokenInMetamask,
    loader,
    showTransactionSucess,
    setShowTransactionSucess,
  } = useContext(Store);
  const { theme } = useTheme();
  const receiveIcon = theme === "light" ? BRAND.logoPathLight : BRAND.logoPath;

  const [activeTab, setActiveTab] = useState("LISTED");
  const [selectedCurrency, setSelectedCurrency] = useState({
    value: "usdt",
    label: "USDT",
    icon: "/images/usdt.svg",
  });
  const [amountToPay, setAmountToPay] = useState("");
  const [amountToReceive, setAmountToReceive] = useState("");
  const [, setReferralBonus] = useState("");
  const [referrerAddress, setReferrerAddress] = useState(ZeroAddress);
  const [timeLeft, setTimeLeft] = useState(getPresaleTimeLeft);
  const [pendingAction, setPendingAction] = useState("");
  const [copiedField, setCopiedField] = useState("");

  const location = useLocation();
  const account = useActiveAccount();
  const { connect } = useConnectModal();
  const address = account?.address;
  const isConnected = Boolean(address);

  const currencyOptions = [
    {
      value: "usdt",
      label: "USDT",
      icon: "/images/usdt.svg",
    },
    {
      value: "usdc",
      label: "USDC",
      icon: "/images/usdc.svg",
    },
  ];

  useEffect(() => {
    refreshPresaleData();
    const interval = window.setInterval(refreshPresaleData, 20_000);
    return () => window.clearInterval(interval);
  }, [refreshPresaleData]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setTimeLeft(getPresaleTimeLeft()),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const section = document.getElementById(location.hash.slice(1));
    section?.scrollIntoView({ behavior: "smooth" });
  }, [location.hash]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const referralFromUrl = searchParams.get("ref");
    const storedReferral = localStorage.getItem("presale_referrer");
    const candidate = referralFromUrl || storedReferral;
    const assignedReferrer = userDatabaseData.referralProfileLoaded
      ? userDatabaseData.referredBy
      : null;

    let newReferrerAddress = ZeroAddress;
    let shouldNotify = false;

    if (assignedReferrer && isAddress(assignedReferrer)) {
      // The backend value is synchronized from buyerReferrer on-chain and must
      // win over a different URL/local referral after assignment.
      newReferrerAddress = getAddress(assignedReferrer);
      localStorage.removeItem("presale_referrer");
    } else if (candidate && isAddress(candidate)) {
      const normalized = getAddress(candidate);
      if (
        address &&
        normalized.toLowerCase() === address.toLowerCase()
      ) {
        localStorage.removeItem("presale_referrer");
        shouldNotify = Boolean(referralFromUrl);
      } else {
        localStorage.setItem("presale_referrer", normalized);
        newReferrerAddress = normalized;
      }
    } else {
      localStorage.removeItem("presale_referrer");
    }

    // Synchronizing with external systems (localStorage and URL) requires setState
    queueMicrotask(() => {
      setReferrerAddress(newReferrerAddress);
      if (shouldNotify) {
        notify.info("You cannot use your own wallet as a referral.");
      }
    });
  }, [
    address,
    location.search,
    notify,
    userDatabaseData.referredBy,
    userDatabaseData.referralProfileLoaded,
  ]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        if (!amountToPay || Number(amountToPay) <= 0) {
          if (!cancelled) {
            setAmountToReceive("");
            setReferralBonus("");
          }
          return;
        }

        const quote = await quoteStablePurchase(
          amountToPay,
          address || ZeroAddress,
          referrerAddress,
        );

        if (!cancelled) {
          setAmountToReceive(quote.formattedBuyerTokens);
          setReferralBonus(quote.formattedReferralTokens);
        }
      } catch (error) {
        console.error("Purchase quote failed:", error);
        if (!cancelled) {
          setAmountToReceive("");
          setReferralBonus("");
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [amountToPay, address, referrerAddress, quoteStablePurchase]);

  const currentPrice = toDisplayNumber(userDatabaseData.currentPrice);
  const configuredNextPrice = Number(import.meta.env.VITE_NEXT_PRESALE_PRICE || 0);
  const nextPrice =
    configuredNextPrice > 0 ? configuredNextPrice : currentPrice * 1.05;

  const saleAllocation = toDisplayNumber(userDatabaseData.saleAllocation);
  const totalRaised = toDisplayNumber(userDatabaseData.totalRaised);
  const buyerTokensSold = toDisplayNumber(
    userDatabaseData.totalBuyerTokensSold,
  );
  const referralTokensPaid = toDisplayNumber(
    userDatabaseData.totalReferralTokensPaid,
  );
  const totalTokensDistributed = toDisplayNumber(
    userDatabaseData.totalTokensDistributed,
  );
  const remainingAllocation = toDisplayNumber(
    userDatabaseData.remainingAllocation,
  );
  const progressPercent =
    saleAllocation > 0
      ? Math.min(100, (totalTokensDistributed / saleAllocation) * 100)
      : 0;
  const saleIsActive = userDatabaseData.loaded && !userDatabaseData.paused;
  const countdownEnded = hasPresaleCountdownEnded(timeLeft);

  const loadingLabel =
    pendingAction === "wallet"
      ? "Opening wallet"
      : pendingAction === "metamask"
        ? "Adding to MetaMask"
        : "Confirming purchase";
  const isActionLoading = loader || Boolean(pendingAction);
  const buyDisabled =
    loader || Boolean(pendingAction) || (isConnected && !saleIsActive);

  const handleBuy = async () => {
    if (!amountToPay || Number(amountToPay) <= 0) {
      notify.error("Please enter a valid payment amount.");
      return;
    }

    if (!saleIsActive) {
      notify.error("Presale is currently paused.");
      return;
    }

    const success = await BuyWithUSDTandUSDC(
      amountToPay,
      selectedCurrency.value === "usdt",
      referrerAddress,
    );

    if (success) {
      setAmountToPay("");
      setAmountToReceive("");
      setReferralBonus("");
    }
  };

  const handleWalletConnect = async () => {
    setPendingAction("wallet");
    try {
      await connect({
        client: thirdwebClient,
        chain: bscChain,
        wallets: thirdwebWallets,
        appMetadata: thirdwebAppMetadata,
        walletConnect: thirdwebWalletConnectOptions,
      });
    } catch {
      // User closed the modal or connection failed; nothing further to do.
    } finally {
      setPendingAction("");
    }
  };

  const handleAddToken = async () => {
    setPendingAction("metamask");
    try {
      await addTokenInMetamask();
    } finally {
      setPendingAction("");
    }
  };

  const handleCopyReferralLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/buy?ref=${address}`);
      setCopiedField("referral");
      notify.success("Referral Link copied.");
      window.setTimeout(() => setCopiedField(""), 1400);
    } catch {
      notify.error("Unable to copy token address.");
    }
  };

  const handleCopyTokenAddress = async () => {
    try {
      await navigator.clipboard.writeText(BRAND.tokenAddress);
      notify.success("Token address copied.");
    } catch {
      notify.error("Unable to copy token address.");
    }
  };

  const stats = useMemo(
    () => [
      ["Raised", `$${formatCompact(totalRaised)}`],
      ["Buyer tokens", formatCompact(buyerTokensSold)],
      ["Referral rewards", formatCompact(referralTokensPaid)],
      ["Remaining", formatCompact(remainingAllocation)],
    ],
    [buyerTokensSold, referralTokensPaid, remainingAllocation, totalRaised],
  );


  const referralLink = `${window.location.origin}/buy?ref=${address}`;

  const presaleCard = (
    <motion.div
      className="sec-img"
      initial={{ scale: 0.8, opacity: 0 }}
      whileInView={{ scale: 1, opacity: 1 }}
      transition={{ duration: 1 }}
      viewport={{ once: true }}
    >
      <div className="containerr">
        <div className="right">
          <div className="presale-urgency-banner">
            Last Chance to Grab 1EYE at 0.05 USDT!
          </div>
          <div className="presaleCard-con">
            <div className="presale-status-row">
              <div
                className={`presale-live-badge ${saleIsActive ? "" : "paused"}`}
              >
                <span />
                {!userDatabaseData.loaded
                  ? "Loading Presale"
                  : saleIsActive
                    ? "Presale Live"
                    : "Presale Paused"}
              </div>
              <div className="presale-round-badge">Round 2</div>
              <div className="presale-bsc-badge">
                <img src="/images/bnb.svg" alt="" width={14} height={14} aria-hidden="true" />
                <span>Powered by BSC</span>
              </div>
            </div>

            <div className="presale-countdown" aria-label="Presale countdown">
              {[
                ["Days", timeLeft.days],
                ["Hours", timeLeft.hours],
                ["Minutes", timeLeft.minutes],
                ["Seconds", timeLeft.seconds],
              ].map(([label, value]) => (
                <div key={label}>
                  <strong>{value}</strong>
                  <small>{label}</small>
                </div>
              ))}
            </div>
            {countdownEnded && <small className="presale-ended-copy">Scheduled presale period has ended.</small>}

            <div className="presale-price-grid">
              <div>
                <span>Current Price</span>
                <strong>{formatTokenPrice(currentPrice)}</strong>
              </div>
              <div>
                <span>Next Price</span>
                <strong>{formatTokenPrice(nextPrice)}</strong>
              </div>
            </div>

            <div className="presale-progress-card">
              <div className="presale-progress-head">
                <span>{BRAND.tokenSymbol} Distribution Progress</span>
                <strong>{progressPercent.toFixed(1)}%</strong>
              </div>
              <div className="presale-progress-track">
                <i style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="presale-progress-foot">
                <span>{formatCompact(totalTokensDistributed)} distributed</span>
                <strong>
                  {formatCompact(totalTokensDistributed)} / {formatCompact(saleAllocation)} {BRAND.tokenSymbol}
                </strong>
              </div>
            </div>

            <div className="presale-contract-stats">
              {stats.map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>

            <div className="address-con">
              <div className="top">
                <div className="line" />
                <GiWarlockEye className="referral-reward-banner__coin" aria-hidden="true" />
                <p>{BRAND.tokenSymbol} Token Address</p>
                <div className="line" />
              </div>
              <div className="input-address">
                <input
                  type="text"
                  readOnly
                  value={`${BRAND.tokenAddress.slice(0, 6)}...${BRAND.tokenAddress.slice(-4)}`}
                />
                <FaRegCopy cursor="pointer" onClick={handleCopyTokenAddress} />
              </div>
            </div>

            <div className="address-con">
              <div className="top">
                <div className="line" />
                <GiTwoCoins className="referral-reward-banner__coin" aria-hidden="true" />
                <p>Referral Link</p>
                <div className="line" />
              </div>

              <div className="input-address">
                <input
                  type="text"
                  readOnly
                  value={`${referralLink.slice(0, 35)}...${referralLink.slice(-8)}`}
                />

                <FaRegCopy
                  cursor="pointer"
                  onClick={handleCopyReferralLink}
                  aria-label="Copy referral link"
                />

              </div>
            </div>

            <div className="center">
              <div className="button-con">
                {currencyOptions.map((currency) => (
                  <p
                    key={currency.value}
                    className={selectedCurrency.value === currency.value ? "active" : ""}
                    onClick={() => setSelectedCurrency(currency)}
                  >
                    <img src={currency.icon} alt={currency.label} />
                    {currency.label}
                  </p>
                ))}
              </div>

              <div className="amount-con">
                <div className="left">
                  <div className="input-box">
                    <label>You pay:</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="0.0"
                        value={amountToPay}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (/^\d*(\.\d{0,18})?$/.test(value)) {
                            setAmountToPay(value);
                          }
                        }}
                      />
                      <img
                        src={selectedCurrency.icon}
                        alt={selectedCurrency.label}
                        width={25}
                        height={25}
                      />
                    </div>
                  </div>
                </div>

                <div className="right">
                  <div className="input-box">
                    <label>You receive:</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        placeholder="0.0"
                        value={amountToReceive}
                        readOnly
                      />
                      <img
                        src={receiveIcon}
                        alt={BRAND.tokenSymbol}
                        width={25}
                        height={25}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div id="presale-buy-action" className="bottom">
              <div className="button-con">
                <button
                  onClick={isConnected ? handleBuy : handleWalletConnect}
                  disabled={buyDisabled}
                  className={`custom-button ${loader || pendingAction === "wallet" ? "loading" : ""}`}
                >
                  {pendingAction === "wallet" ? (
                    <CosmicEyeLoader label="Opening wallet" />
                  ) : loader ? (
                    <CosmicEyeLoader label="Confirming purchase" />
                  ) : !isConnected ? (
                    <>
                      <FaWallet aria-hidden="true" />
                      <span style={{ color: "#ffffff" }}>Connect Wallet</span>
                    </>
                  ) : saleIsActive ? (
                    "BUY"
                  ) : (
                    "PRESALE PAUSED"
                  )}
                </button>

                <button
                  onClick={handleAddToken}
                  disabled={loader || Boolean(pendingAction)}
                  className={`buy-button ${pendingAction === "metamask" ? "loading" : ""}`}
                >
                  {pendingAction === "metamask" ? (
                    <CosmicEyeLoader label="Adding to MetaMask" />
                  ) : (
                    `Add ${BRAND.tokenSymbol} in MetaMask`
                  )}
                </button>
              </div>
              <div className="presale-trust-row">
                <span>On-chain allocation</span>
                <span>Secure wallet checkout</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (cardOnly) {
    return (
      <>
        {isActionLoading && <CosmicLoadingOverlay label={loadingLabel} />}
        <div id="presale-card" className="hero-presale-card-shell buy-page">
          {presaleCard}
        </div>
        <TransactionSucessModal
          showTransactionSucess={showTransactionSucess}
          setShowTransactionSucess={setShowTransactionSucess}
        />
      </>
    );
  }

  return (
    <>
      {isActionLoading && <CosmicLoadingOverlay label={loadingLabel} />}
      <div className="sec-main-container buy-page">
        {presaleCard}

        <motion.div
          className="sec-text"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 3 }}
          viewport={{ once: true }}
        >
          <div className="links">
            <ul>
              <li>
                <Link
                  className={activeTab === "LISTED" ? "active" : ""}
                  onClick={() => setActiveTab("LISTED")}
                >
                  LISTED
                </Link>
              </li>
              <li>
                <Link
                  className={activeTab === "COMING_SOON" ? "active" : ""}
                  onClick={() => setActiveTab("COMING_SOON")}
                >
                  COMING SOON
                </Link>
              </li>
            </ul>
          </div>

          <motion.div
            className="text"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
          >
            <h1>Get {BRAND.tokenSymbol}</h1>
            <p>
              Alexandrite&apos;s, one of the rarest and most valuable gemstones
              in the world. Designed to ensure stability, transparency, and
              trust, {BRAND.name} presents a secure and dependable alternative
              in the cryptocurrency space.
            </p>
          </motion.div>

          {activeTab === "LISTED" && (
            <motion.div
              className="small-cards"
              initial={{ y: 100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              <Smcard icon={icon2} text="PancakeSwap" />
              <Smcard icon={icon15} text="SushiSwap" />
              <Smcard icon={icon5} text="KoinPark" />
              <Smcard icon={icon8} text="Biswap" />
              <Smcard icon={icon9} text="Thena" />
              <Smcard icon={icon4} text="CoinMarketCap" />
              <Smcard icon={icon3} text="Coingecko" />
            </motion.div>
          )}

          {activeTab === "COMING_SOON" && (
            <motion.div
              className="small-cards"
              initial={{ y: 100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
            >
              <Smcard icon={icon1} text="Binance" />
              <Smcard icon={icon6} text="NovaDex" />
              <Smcard icon={icon7} text="Foxbit" />
              <Smcard icon={icon10} text="Gate.io" />
              <Smcard icon={icon11} text="BabySwap" />
              <Smcard icon={icon12} text="ApesSwap" />
              <Smcard icon={icon13} text="Bitso" />
              <Smcard icon={icon14} text="Dodoex" />
            </motion.div>
          )}
        </motion.div>
      </div>

      <TransactionSucessModal
        showTransactionSucess={showTransactionSucess}
        setShowTransactionSucess={setShowTransactionSucess}
      />
    </>
  );
}

export default SectionThree;
